/**
 * VisibilityPage — Phase 1C
 *
 * Dedicated, restrained page for candidates to control resume visibility.
 *
 * Reuses the existing PUT /api/resume endpoint with `{ visible }` payload —
 * the same call the previous dashboard visibility card invoked. No new
 * endpoint, no schema change, no new visibility states.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function VisibilityPage() {
  const [user, setUser] = useState(null);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, resumeRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setUser(userRes.data);
        setResume(resumeRes.data);
      } catch {
        toast.error('Could not load your visibility settings.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggle = async () => {
    if (!resume) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/resume`,
        { visible: !resume.visible },
        { withCredentials: true }
      );
      setResume(response.data);
      toast.success(`Resume ${response.data.visible ? 'visible' : 'hidden'}`);
    } catch {
      toast.error('Failed to update visibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={Eye}
          title="Visibility"
          subtitle="Control how employers can discover your profile."
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : (
          <Card className="p-6 border border-slate-200 shadow-sm" data-testid="visibility-page-card">
            <div className="flex items-start justify-between gap-6 mb-4">
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">Employer visibility</h3>
                <p className="text-sm text-slate-600">
                  When visible, approved employers in your region can discover your profile and request access to your resume.
                </p>
              </div>
              <div className="flex-shrink-0">
                {resume?.visible ? (
                  <Eye className="w-6 h-6 text-brand" strokeWidth={1.5} />
                ) : (
                  <EyeOff className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                )}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">Visible to employers</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {resume?.visible ? 'Approved employers can discover your profile.' : 'Your profile is private.'}
                </p>
              </div>
              <Button
                data-testid="visibility-page-toggle-btn"
                onClick={toggle}
                variant="outline"
                size="sm"
                disabled={!resume?.is_complete || saving}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 min-w-[120px]"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : resume?.visible ? (
                  'Hide profile'
                ) : (
                  'Make visible'
                )}
              </Button>
            </div>

            {!resume?.is_complete && (
              <p className="mt-3 text-xs text-slate-500">
                Finish your resume to enable visibility.
              </p>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default VisibilityPage;
